import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { usePrice } from '@k3/core';

export type DiscountInformation = {
  storeId: 2 | 50; // Wenn Kioskmode im Haus Waltersdorf, dann 2. Wenn Höffner Online, dann 50
  discountLock: false;
  voucherLocked: '1';
  discounts: {
    discountType: 'A' | 'C';
    discountLevel: 101;
    percentage: number;
    voucherRedemptionForbidden: true;
    ignoreDiscountLock: false;
    discountValue?: number;
  }[];
};

export type K3Discount = {
  discountPercentage: number;
  discountInformation: DiscountInformation;
};

export type DiscountContextType = {
  discount: K3Discount | null;
  setDiscount: (discount: K3Discount | null) => void;
};

const discountContext = createContext<DiscountContextType>({
  discount: null,
  setDiscount: () => {
    console.error('Discount Provider missing');
  },
});

export const useDiscountContext = () => useContext(discountContext);

export interface DiscountFrontendInformation {
  discount: K3Discount;
  discountPercent: number;
  discountValue: number;
  discountedPrice: number;
  fullPrice: number;
  hasDiscount: boolean;
  aDiscount?: K3Discount['discountInformation']['discounts'][0];
  cDiscount?: K3Discount['discountInformation']['discounts'][0];
}

export const getDiscountInformation = (
  price: number,
  discount: K3Discount
): DiscountFrontendInformation => {
  const discountPercent = discount?.discountPercentage ?? 0;
  const discountValue = +(price * (discountPercent / 100));
  const discountedPrice = +(price - discountValue);
  const hasDiscount = !!discountPercent;

  return {
    discount,
    discountPercent,
    discountValue,
    discountedPrice,
    fullPrice: price,
    hasDiscount,
    aDiscount: discount?.discountInformation?.discounts?.find(
      (discount) => discount.discountType === 'A'
    ),
    cDiscount: discount?.discountInformation?.discounts?.find(
      (discount) => discount.discountType === 'C'
    ),
  };
};

export const useDiscountPrice = () => {
  const { discount } = useDiscountContext();
  const price = usePrice();

  if (!discount) {
    return null;
  }

  return getDiscountInformation(price, discount);
};

export const DiscountProvider = (props: PropsWithChildren) => {
  const [discount, setDiscount] =
    useState<DiscountContextType['discount']>(null);
  return (
    <discountContext.Provider value={{ discount, setDiscount }}>
      {props.children}
    </discountContext.Provider>
  );
};
